// ============================================================================
// WHERE THE MONEY IS WAITING - the lead slot.
// ----------------------------------------------------------------------------
// Lane down the side, time since arrival across the top. The two questions the
// old board asked separately ("which lane" and "how old") are one question, and
// crossing them is that question.
//
// Two editorial decisions are load-bearing and are stated here so a later agent
// does not "fix" them:
//
// 1. UNDECIDED LANES ONLY. A decided job is not waiting for anything, so folding
//    it in would inflate the only headline on the page with money that is
//    already resolved.
// 2. respectsPeriod: false. "Lanes among jobs created in the last three months"
//    is a different and less useful number than "where the book stands now", so
//    this panel reads ctx.allRows and the head carries an "all time" chip.
// ============================================================================

import { censusPhrase } from '../model';
import type {
    AnalysisContext,
    Cell,
    DrillTarget,
    LaneKey,
    PanelDef,
    PanelReadiness,
    PanelSeries,
} from '../registry';
import { money, moneyCompact, type DerivedRow } from '../model';

/** The marginal column. Not a fifth age band: a per-lane roll-up of the four. */
const ALL_COL = 'all';

/** Past three weeks, the clause the headline speaks. Band boundary, not a guess. */
const STALE_FROM_DAYS = 22;

interface BandDef {
    key: string;
    /** Column head. Matches the spec's furniture verbatim. */
    label: string;
    /** Spoken form, used for the cell label the drawer titles itself with. */
    longLabel: string;
    holds(days: number): boolean;
}

const BANDS: BandDef[] = [
    { key: '0-7', label: '0-7d', longLabel: '0 to 7 days', holds: d => d <= 7 },
    { key: '8-21', label: '8-21d', longLabel: '8 to 21 days', holds: d => d >= 8 && d <= 21 },
    { key: '22-60', label: '22-60d', longLabel: '22 to 60 days', holds: d => d >= 22 && d <= 60 },
    { key: '60+', label: '60d+', longLabel: 'past 60 days', holds: d => d > 60 },
];

interface LaneDef {
    key: LaneKey;
    label: string;
    tone: string;
}

/**
 * Lane keys, labels and tones are the Run Sheet's, copied from its LANES array
 * (DashboardPage.tsx). Identity has to survive the trip between the two pages or
 * the operator has to relearn the colours, so these are the same four strings.
 */
const UNDECIDED_LANES: LaneDef[] = [
    { key: 'finish', label: 'Needs finishing', tone: 'var(--qm-lane-finish)' },
    { key: 'send', label: 'Ready to send', tone: 'var(--qm-lane-send)' },
    { key: 'waiting', label: 'Awaiting answer', tone: 'var(--qm-lane-waiting)' },
];

const UNDECIDED_KEYS: LaneKey[] = UNDECIDED_LANES.map(l => l.key);

function isUndecided(row: DerivedRow): boolean {
    return UNDECIDED_KEYS.includes(row.lane);
}

function undecidedOf(rows: DerivedRow[]): DerivedRow[] {
    return rows.filter(isUndecided);
}

function bandKeyFor(days: number): string {
    for (const b of BANDS) if (b.holds(days)) return b.key;
    // Unreachable while the four bands tile [0, infinity). Kept so a future band
    // edit cannot silently drop rows out of the matrix.
    return '60+';
}

/** Values are carried on DerivedRow.value. Never recomputed here: see model.ts. */
function sumValue(rows: DerivedRow[]): number {
    return rows.reduce((s, r) => s + r.value, 0);
}

/**
 * Read only to choose the WORDING of the working line. The figure itself always
 * comes from DerivedRow.value, so this can never move a number.
 */
function isGstInclusive(row: DerivedRow): boolean {
    const raw = row.raw;
    const flag = raw?.quoteData?.isGstInclusive ?? raw?.scopeGstInclusive ?? raw?.scope_gst_inclusive;
    return flag === undefined || flag === null ? true : Boolean(flag);
}

/**
 * THE WORKING LINE. The divisor clause is conditional because a book with a
 * mixed GST mode would otherwise be told a flat lie about its own arithmetic.
 */
function workingLine(picked: DerivedRow[]): string {
    const n = picked.length;
    const total = money(sumValue(picked));
    if (n === 0) return `0 rows  =  ${total} ex GST`;
    const inclusive = picked.filter(isGstInclusive).length;
    const divisor = inclusive === n
        ? ' ÷ 1.1'
        : inclusive === 0
            ? ''
            : ' ÷ 1.1 where the scope is GST inclusive';
    return `${n} ${n === 1 ? 'row' : 'rows'} × sum(scope[].amount)${divisor}  =  ${total} ex GST`;
}

/** "Awaiting answer" reaches the note as "awaiting-answer", per the spec's copy. */
function laneSlug(label: string): string {
    return label.toLowerCase().replace(/\s+/g, '-');
}

function bandDrill(lane: LaneDef): DrillTarget {
    return {
        lane: lane.key,
        query: '',
        // Oldest first: the operator opens an age band to chase the stale ones,
        // and the run sheet cannot hold the band itself.
        sort: 'oldest',
        exact: false,
        note: `The run sheet cannot filter by age. This opens the ${laneSlug(lane.label)} lane, oldest first.`,
    };
}

function laneDrill(lane: LaneDef): DrillTarget {
    return { lane: lane.key, query: '', sort: 'oldest', exact: false };
}

function compute(ctx: AnalysisContext): PanelSeries {
    const population = undecidedOf(ctx.allRows);
    const total = { count: population.length, value: sumValue(population) };

    const rowKeys = UNDECIDED_LANES.map(l => ({ key: l.key, label: l.label, tone: l.tone }));
    const colKeys = [
        ...BANDS.map(b => ({ key: b.key, label: b.label })),
        { key: ALL_COL, label: 'All' },
    ];

    const stale = population.filter(r => r.days >= STALE_FROM_DAYS).length;
    const jobs = `${total.count} ${total.count === 1 ? 'job' : 'jobs'}`;
    const headline = {
        figure: moneyCompact(total.value),
        caps: 'WAITING ON A DECISION',
        clause: total.count === 0
            ? 'Nothing is waiting on a decision.'
            : stale > 0
                ? `${jobs}, ${stale} of them past three weeks.`
                : `${jobs}, none past three weeks.`,
    };

    if (total.count === 0) {
        return {
            cells: [],
            rowKeys,
            colKeys,
            total,
            headline,
            emptySeries: 'Nothing is waiting on a decision.',
        };
    }

    // Band cells first, because their spread sets the tint scale.
    const bandCells: Cell[] = [];
    for (const lane of UNDECIDED_LANES) {
        for (const band of BANDS) {
            const picked = population.filter(r => r.lane === lane.key && bandKeyFor(r.days) === band.key);
            bandCells.push({
                key: `${lane.key}:${band.key}`,
                label: `${lane.label}, ${band.longLabel}`,
                count: picked.length,
                value: sumValue(picked),
                weight: 0,
                tone: lane.tone,
                rowKey: lane.key,
                colKey: band.key,
                select: rows => rows.filter(
                    r => isUndecided(r) && r.lane === lane.key && bandKeyFor(r.days) === band.key,
                ),
                formula: workingLine,
                drill: bandDrill(lane),
            });
        }
    }

    // Two tint scales, deliberately. The All column is a marginal, so folding it
    // into the same denominator would crush every band cell to near invisible.
    const maxBand = bandCells.reduce((m, c) => Math.max(m, c.value), 0);
    for (const c of bandCells) c.weight = maxBand > 0 ? c.value / maxBand : 0;

    const allCells: Cell[] = UNDECIDED_LANES.map(lane => {
        const picked = population.filter(r => r.lane === lane.key);
        return {
            key: `${lane.key}:${ALL_COL}`,
            label: `${lane.label}, every age`,
            count: picked.length,
            value: sumValue(picked),
            weight: 0,
            tone: lane.tone,
            rowKey: lane.key,
            colKey: ALL_COL,
            select: rows => rows.filter(r => isUndecided(r) && r.lane === lane.key),
            formula: workingLine,
            drill: laneDrill(lane),
        };
    });
    const maxAll = allCells.reduce((m, c) => Math.max(m, c.value), 0);
    for (const c of allCells) c.weight = maxAll > 0 ? c.value / maxAll : 0;

    return { cells: [...bandCells, ...allCells], rowKeys, colKeys, total, headline };
}

/**
 * The ageing axis is only as good as created_at. A row with no created date
 * carries days: 0 and would sit in the 0-7d band pretending to be fresh, so the
 * panel says so rather than letting the matrix read clean.
 */
function readiness(_s: PanelSeries, ctx: AnalysisContext): PanelReadiness {
    const population = undecidedOf(ctx.allRows);
    if (population.length === 0) return 'live';
    const dated = population.filter(r => r.createdMs > 0).length;
    if (dated === 0) return 'dormant';
    if (dated < population.length) return 'thin';
    return 'live';
}

function activation(_s: PanelSeries, ctx: AnalysisContext): string | null {
    const population = undecidedOf(ctx.allRows);
    const undated = population.filter(r => !r.createdMs).length;
    if (population.length === 0 || undated === 0) return null;
    if (undated === population.length) {
        return 'No undecided job carries a created date, so the age bands cannot be built. '
            + 'A created date on every job, which QuoteMaker stamps on anything it starts itself, switches this on.';
    }
    return `${undated} undecided ${undated === 1 ? 'job carries' : 'jobs carry'} no created date, `
        + `so ${undated === 1 ? 'it counts' : 'they count'} in the 0-7d band. `
        + 'A created date on every imported job makes the ageing exact.';
}

function stamp(s: PanelSeries, ctx: AnalysisContext): string {
    return `${censusPhrase(s.total.count, ctx.window.capped, 'undecided')} · laneOf() · created_at · scope[].amount ex GST`;
}

function csv(s: PanelSeries): { headers: string[]; rows: Record<string, string | number>[] } {
    const headers = ['Lane', 'Age band', 'Jobs', 'Value ex GST'];
    const laneLabel = new Map(UNDECIDED_LANES.map(l => [String(l.key), l.label]));
    const colLabel = new Map((s.colKeys ?? []).map(c => [c.key, c.label]));
    // The "all" column is a per-lane MARGINAL: it re-counts the same jobs the four
    // band cells already carry. Exporting both means the Jobs column sums to
    // exactly twice the real figure in a spreadsheet, which is the kind of wrong
    // number that reaches an accountant. Only the partitioning cells are exported.
    const partitioning = s.cells.filter(c => c.colKey !== 'all');
    return {
        headers,
        rows: partitioning.map(c => ({
            Lane: laneLabel.get(String(c.rowKey ?? '')) ?? String(c.rowKey ?? ''),
            'Age band': colLabel.get(String(c.colKey ?? '')) ?? String(c.colKey ?? ''),
            Jobs: c.count,
            'Value ex GST': Math.round(c.value),
        })),
    };
}

function escapeXml(s: string): string {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
}

function clamp(n: number, lo: number, hi: number): number {
    return Math.min(hi, Math.max(lo, n));
}

function round(n: number): number {
    return Math.round(n * 100) / 100;
}

/**
 * Static SVG. The print document is served script-src 'none', so every number
 * below is geometry resolved in JS before serialisation: no canvas, no measure
 * pass, nothing to run. Colour is currentColor plus the accent variable the
 * report injects, so flipping the brand retints the printed matrix and no hex
 * literal is authored here.
 */
function printSvg(s: PanelSeries, w: number, h: number): string {
    const width = Math.max(240, Math.round(w || 0));
    const height = Math.max(80, Math.round(h || 0));
    const open = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}"`
        + ` width="${width}" height="${height}" role="img" fill="currentColor"`;

    const rowKeys = s.rowKeys ?? [];
    const colKeys = s.colKeys ?? [];
    const bandCols = colKeys.filter(c => c.key !== ALL_COL);

    if (s.cells.length === 0 || rowKeys.length === 0 || bandCols.length === 0) {
        const line = escapeXml(s.emptySeries || 'Nothing is waiting on a decision.');
        return `${open} aria-label="${line}"><text x="0" y="${round(height / 2)}"`
            + ` font-size="11" dominant-baseline="central" fill-opacity="0.7">${line}</text></svg>`;
    }

    const gap = 3;
    const headH = 14;
    const labelW = clamp(Math.round(width * 0.24), 84, 168);
    const totalW = colKeys.length > bandCols.length ? clamp(Math.round(width * 0.12), 44, 84) : 0;
    const trackW = Math.max(40, width - labelW - totalW - gap * (bandCols.length + 1));
    const bandW = trackW / bandCols.length;
    const bodyH = Math.max(24, height - headH - gap);
    const rowH = (bodyH - gap * (rowKeys.length - 1)) / rowKeys.length;

    const colX = (i: number): number => labelW + gap + i * (bandW + gap);
    const totalX = colX(bandCols.length);
    const rowY = (j: number): number => headH + gap + j * (rowH + gap);

    const byKey = new Map<string, Cell>(s.cells.map(c => [c.key, c]));

    const parts: string[] = [];

    parts.push(`<title>${escapeXml('Undecided work by lane and age')}</title>`);

    bandCols.forEach((c, i) => {
        parts.push(`<text x="${round(colX(i) + bandW)}" y="${headH - 3}" text-anchor="end"`
            + ` font-size="8.5" font-weight="700" fill-opacity="0.6"`
            + ` letter-spacing="0.4">${escapeXml(c.label)}</text>`);
    });
    if (totalW > 0) {
        parts.push(`<text x="${round(totalX + totalW)}" y="${headH - 3}" text-anchor="end"`
            + ` font-size="8.5" font-weight="700" fill-opacity="0.6" letter-spacing="0.4">All</text>`);
    }

    rowKeys.forEach((row, j) => {
        const y = rowY(j);
        const mid = round(y + rowH / 2);
        parts.push(`<text x="0" y="${mid}" font-size="8.5" font-weight="800"`
            + ` dominant-baseline="central" letter-spacing="0.3">${escapeXml(row.label)}</text>`);

        bandCols.forEach((col, i) => {
            const cell = byKey.get(`${row.key}:${col.key}`);
            if (!cell) return;
            const x = round(colX(i));
            // Same ramp as the screen: alpha = weight x 26%, magnitude only.
            const alpha = round(clamp(cell.weight, 0, 1) * 0.26);
            parts.push(`<rect x="${x}" y="${round(y)}" width="${round(bandW)}" height="${round(rowH)}"`
                + ` rx="3" fill="var(--qm-accent, currentColor)" fill-opacity="${alpha}"`
                + ` stroke="currentColor" stroke-opacity="0.12" stroke-width="0.5"/>`);
            parts.push(`<text x="${round(x + bandW / 2)}" y="${mid}" text-anchor="middle"`
                + ` font-size="9.5" font-weight="800" dominant-baseline="central">${cell.count}</text>`);
        });

        if (totalW > 0) {
            const cell = byKey.get(`${row.key}:${ALL_COL}`);
            if (cell) {
                parts.push(`<text x="${round(totalX + totalW)}" y="${mid}" text-anchor="end"`
                    + ` font-size="9" font-weight="800">${cell.count}</text>`);
            }
        }
    });

    const spoken = rowKeys
        .map(r => {
            const cell = byKey.get(`${r.key}:${ALL_COL}`);
            return `${r.label} ${cell ? cell.count : 0}`;
        })
        .join(', ');
    const aria = escapeXml(`Undecided work by lane and age: ${spoken}`);

    return `${open} aria-label="${aria}">${parts.join('')}</svg>`;
}

const ageingMatrix: PanelDef = {
    id: 'ageing-matrix',
    title: 'Where the money is waiting',
    hint: 'undecided jobs by lane and time since arrival',
    group: 'money',
    defaultSpan: 12,
    minSpan: 12,
    slim: false,
    mark: 'matrix',
    respectsPeriod: false,

    compute,
    readiness,
    activation,
    stamp,
    // The stamp counts undecided rows only, so the stamp door must prove
    // undecided rows only. See PanelDef.population.
    population: undecidedOf,

    fields: ['scope[].amount', 'scopeGstInclusive', 'createdAt', 'quoteStatus', 'status', 'quoteComplete'],
    caveats: [
        'Decided jobs are left out. A won, lost or expired job is not waiting for anything.',
        'Age is time since the job arrived, not time since the quote went out.',
    ],

    csv,
    printSvg,
};

export default ageingMatrix;
